/**
 * PriceCurveChart — canvas-based D3 line chart for DA/Intraday price curves.
 *
 * Two canvases: main (grid + reference areas/lines + series) and hover
 * (crosshair, redrawn on every mousemove without touching the main canvas).
 * A transparent SVG overlay captures mouse events.
 */
import { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import * as d3 from 'd3'
import { format, startOfDay } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { InfoTip } from '@/components/ui/info-tip'
import { cn } from '@/lib/utils'
import type { PricePoint } from '@/services/api/priceApi'
import type { SlotResult } from '@/lib/ev-optimizer'
import type { SessionRow } from '@/components/simulator/SessionBreakdownTable'
import { SCENARIO_LABELS, SCENARIO_DESCRIPTIONS, SCENARIO_COLORS } from '@/lib/simulator-scenarios'

export interface SessionOverlay {
  pluggedAtMs: number
  unplugMs: number
  schedule: SlotResult[]
}

interface Props {
  daprices: PricePoint[]
  intradayPrices?: PricePoint[]
  idaSeries?: Record<string, PricePoint[]>
  /** Blended per-slot min(DA, ID-Cfull) curve — the "Cross-Market" scenario. Kept
   * separate from idaSeries so it never becomes a spurious IDA series in the
   * optimizer, summary cards, or session table. */
  crossPrices?: PricePoint[]
  /** One overlay per selected session */
  sessionOverlays?: SessionOverlay[]
  flatRate: number
  /** Visible time window [startMs, endMs]. Controls the X axis domain. */
  xDomain: [number, number]
  onDomainChange?: (domain: [number, number]) => void
  allSessions?: SessionRow[]
  /** Actual charging density from real chargingPhases data */
  actualDensity?: Map<number, { kWh: number; count: number }>
}

type ChartPoint = { ts: number } & { [key: string]: number | undefined }

// Static series (always present)
const STATIC_SERIES = [
  { key: 'da', label: 'Day-Ahead', color: '#3b82f6' },
]

// Color for the one dynamic IDA series the simulator ever passes in (ID-Cfull, or its
// uploaded-CSV alias) — DA and Cross-Market are the only other price lines shown.
const IDA_SERIES_COLOR = '#8b5cf6'

const MARGIN = { top: 10, right: 52, bottom: 40, left: 55 }
const HEIGHT = 280
const TICK_FONT = '12px sans-serif'

const SLOT_MS = 15 * 60 * 1000

const START_OF_2026 = new Date('2026-01-01T00:00:00Z').getTime()

const SERIES_DESCRIPTIONS: Record<string, { epex?: string; smard?: string }> = {
  da:          { epex: 'Day-ahead prices (EPEX Spot)', smard: 'Day-ahead prices (SMARD.de)' },
  id:          { smard: 'Intraday Continuous Average (SMARD.de)' },
  'ID-Cfull':  { epex: 'Average of all prices for the delivery period' },
  IDFull:      { epex: 'Average of all prices for the delivery period' },
  cross:       { epex: SCENARIO_DESCRIPTIONS.cross, smard: SCENARIO_DESCRIPTIONS.cross },
}

export function PriceCurveChart({
  daprices,
  intradayPrices = [],
  idaSeries,
  crossPrices,
  sessionOverlays = [],
  flatRate,
  xDomain,
  onDomainChange,
  allSessions = [],
  actualDensity,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const hoverCanvasRef = useRef<HTMLCanvasElement>(null)
  const overlayRef = useRef<SVGSVGElement>(null)

  const [width, setWidth] = useState(600)
  const [hidden, setHidden] = useState<Record<string, boolean>>({})
  const [showDensity, setShowDensity] = useState(true)
  const [showActual, setShowActual] = useState(true)
  const [tooltip, setTooltip] = useState<{ x: number; y: number; ts: number } | null>(null)

  // ── Dynamic IDA series derived from prop ───────────────────────────────────
  const idaSeriesEntries = useMemo(
    () =>
      Object.keys(idaSeries ?? {}).map((key) => ({
        key,
        label: key,
        color: IDA_SERIES_COLOR,
      })),
    [idaSeries]
  )

  // All series (static + dynamic) — used for scale computation and tooltip
  // "ID Cont." only included when intraday data exists for the range
  const allSeries = useMemo(
    () => [
      STATIC_SERIES[0],
      ...idaSeriesEntries,
      ...(crossPrices && crossPrices.length > 0
        ? [{ key: 'cross', label: SCENARIO_LABELS.cross, color: SCENARIO_COLORS.cross }]
        : []),
    ],
    [idaSeriesEntries, crossPrices]
  )

  const isEpex = xDomain[1] <= START_OF_2026

  // ── Panning state ──────────────────────────────────────────────────────────
  const dragRef = useRef<{ clientX: number; domain: [number, number] } | null>(null)
  const [isPanning, setIsPanning] = useState(false)

  // ── Session density per 15-min slot ───────────────────────────────────────

  const slotDensity = useMemo(() => {
    const map = new Map<number, { kWh: number; count: number }>()
    for (const session of allSessions) {
      for (const slot of session.result.schedule) {
        if (!slot.selected || slot.energyKwh <= 0) continue
        const existing = map.get(slot.timestampMs)
        if (existing) {
          existing.kWh += slot.energyKwh
          existing.count += 1
        } else {
          map.set(slot.timestampMs, { kWh: slot.energyKwh, count: 1 })
        }
      }
    }
    return map
  }, [allSessions])

  const maxSlotKwh = useMemo(() => {
    let max = 0
    for (const { kWh } of slotDensity.values()) {
      if (kWh > max) max = kWh
    }
    return max
  }, [slotDensity])

  const maxActualKwh = useMemo(() => {
    if (!actualDensity) return 0
    let max = 0
    for (const { kWh } of actualDensity.values()) {
      if (kWh > max) max = kWh
    }
    return max
  }, [actualDensity])

  // Combined max for a shared kWh scale on the right axis
  const maxDensityKwh = Math.max(maxSlotKwh, maxActualKwh)

  const plotW = Math.max(1, width - MARGIN.left - MARGIN.right)
  const plotH = Math.max(1, HEIGHT - MARGIN.top - MARGIN.bottom)

  // ── Data ──────────────────────────────────────────────────────────────────

  const chartData = useMemo<ChartPoint[]>(() => {
    const map = new Map<number, ChartPoint>()
    for (const p of daprices) {
      map.set(p.timestampMs, { ts: p.timestampMs, da: parseFloat((p.priceEurKwh * 100).toFixed(3)) })
    }
    for (const p of intradayPrices) {
      const existing = map.get(p.timestampMs)
      const ct = parseFloat((p.priceEurKwh * 100).toFixed(3))
      if (existing) { existing.id = ct } else { map.set(p.timestampMs, { ts: p.timestampMs, id: ct }) }
    }
    for (const { key } of idaSeriesEntries) {
      for (const p of idaSeries![key]) {
        const ct = parseFloat((p.priceEurKwh * 100).toFixed(3))
        const existing = map.get(p.timestampMs)
        if (existing) { existing[key] = ct } else { map.set(p.timestampMs, { ts: p.timestampMs, [key]: ct }) }
      }
    }
    for (const p of crossPrices ?? []) {
      const ct = parseFloat((p.priceEurKwh * 100).toFixed(3))
      const existing = map.get(p.timestampMs)
      if (existing) { existing.cross = ct } else { map.set(p.timestampMs, { ts: p.timestampMs, cross: ct }) }
    }
    return Array.from(map.values()).sort((a, b) => a.ts - b.ts)
  }, [daprices, intradayPrices, idaSeries, idaSeriesEntries, crossPrices])

  const xMin = xDomain[0]
  const xMax = xDomain[1]

  const bands = useMemo(
    () =>
      daprices.map((p, i) => ({
        x1: p.timestampMs,
        x2: daprices[i + 1]?.timestampMs ?? p.timestampMs + 15 * 60 * 1000,
        cheap: p.priceEurKwh < flatRate,
      })),
    [daprices, flatRate]
  )

  const chargingSlots = useMemo(
    () => sessionOverlays.flatMap((o) => o.schedule.filter((s) => s.selected && s.energyKwh > 0)),
    [sessionOverlays]
  )

  // ── ResizeObserver ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w && w > 0) setWidth((prev) => (prev === Math.floor(w) ? prev : Math.floor(w)))
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  // ── Scales ─────────────────────────────────────────────────────────────────

  const buildScales = useCallback((): {
    xS: d3.ScaleLinear<number, number>
    yS: d3.ScaleLinear<number, number>
  } => {
    const xS = d3.scaleLinear().domain([xMin, xMax]).range([0, plotW])
    const visibleVals = chartData
      .filter((d) => d.ts >= xMin && d.ts <= xMax)
      .flatMap((d) =>
        allSeries.filter((s) => !hidden[s.key]).flatMap((s) => {
          const v = d[s.key]
          return v !== undefined ? [v] : []
        })
      )
    const yMin = Math.min(0, d3.min(visibleVals) ?? 0)
    const yMax = d3.max(visibleVals) ?? 1
    const yS = d3.scaleLinear().domain([yMin, yMax * 1.05]).nice().range([plotH, 0])
    return { xS, yS }
  }, [xMin, xMax, plotW, plotH, chartData, hidden, allSeries])

  // ── Main canvas: grid + overlays + series ──────────────────────────────────
  // NOTE: `tooltip` is intentionally NOT in this dep array.

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || chartData.length === 0) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = width * dpr
    canvas.height = HEIGHT * dpr

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, width, HEIGHT)

    const { xS, yS } = buildScales()
    const flatLineCt = flatRate * 100
    const now = Date.now()
    const showNow = now >= xMin && now <= xMax

    ctx.save()
    ctx.translate(MARGIN.left, MARGIN.top)

    // ── Reference areas (drawn before grid so they sit behind everything) ──

    for (const b of bands) {
      const bx1 = xS(b.x1)
      const bx2 = xS(b.x2)
      ctx.fillStyle = b.cheap ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)'
      ctx.fillRect(bx1, 0, bx2 - bx1, plotH)
    }

    // ── Actual charging bars (gray, drawn first so optimized sits on top) ────

    if (showActual && actualDensity && maxActualKwh > 0) {
      for (const [slotMs, { kWh }] of actualDensity.entries()) {
        const t = kWh / maxDensityKwh
        if (t < 0.02) continue
        const alpha = 0.18 + t * 0.35
        ctx.fillStyle = `rgba(100,116,139,${alpha.toFixed(2)})`
        const barH = Math.round(t * plotH)
        const x1 = xS(slotMs)
        const x2 = xS(slotMs + SLOT_MS)
        ctx.fillRect(x1, plotH - barH, Math.max(1, x2 - x1 - 1), barH)
      }
    }

    // ── Optimised density bars (warm/cool, drawn on top of actual) ───────────

    if (showDensity && maxSlotKwh > 0) {
      for (const [slotMs, { kWh }] of slotDensity.entries()) {
        const t = kWh / maxDensityKwh
        if (t < 0.02) continue
        const rgb = d3.rgb(d3.interpolateRdYlBu(1 - t))
        const alpha = 0.25 + t * 0.55
        ctx.fillStyle = `rgba(${Math.round(rgb.r)},${Math.round(rgb.g)},${Math.round(rgb.b)},${alpha.toFixed(2)})`
        const barH = Math.round(t * plotH)
        const x1 = xS(slotMs)
        const x2 = xS(slotMs + SLOT_MS)
        ctx.fillRect(x1, plotH - barH, Math.max(1, x2 - x1 - 1), barH)
      }
    }

    if (sessionOverlays.length > 0 || chargingSlots.length > 0) {
      ctx.save()
      ctx.beginPath()
      ctx.rect(0, 0, plotW, plotH)
      ctx.clip()

      for (const overlay of sessionOverlays) {
        const sx1 = xS(overlay.pluggedAtMs)
        const sx2 = xS(overlay.unplugMs)
        ctx.fillStyle = 'rgba(99,102,241,0.06)'
        ctx.fillRect(sx1, 0, sx2 - sx1, plotH)
      }

      for (const slot of chargingSlots) {
        const sx1 = xS(slot.timestampMs)
        const sx2 = xS(slot.timestampMs + 15 * 60 * 1000)
        ctx.fillStyle = 'rgba(34,197,94,0.45)'
        ctx.fillRect(sx1, 0, sx2 - sx1, plotH)
      }

      ctx.restore()
    }

    // ── Y grid + ticks ─────────────────────────────────────────────────────

    const yTicks = yS.ticks(5)
    ctx.font = TICK_FONT
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    for (const t of yTicks) {
      const py = yS(t)
      ctx.strokeStyle = 'rgba(128,128,128,0.12)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(0, py)
      ctx.lineTo(plotW, py)
      ctx.stroke()
      ctx.fillStyle = 'rgba(128,128,128,0.85)'
      ctx.fillText(t.toFixed(1), -8, py)
    }

    // Y axis label
    ctx.save()
    ctx.translate(-38, plotH / 2)
    ctx.rotate(-Math.PI / 2)
    ctx.font = '11px sans-serif'
    ctx.fillStyle = 'rgba(128,128,128,0.85)'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('ct/kWh', 0, 0)
    ctx.restore()

    // ── Right Y-axis: kWh scale for density bars ────────────────────────────

    if ((showDensity || showActual) && maxDensityKwh > 0) {
      const kWhYS = d3.scaleLinear().domain([0, maxDensityKwh]).range([plotH, 0]).nice()
      const kWhTicks = kWhYS.ticks(5)
      ctx.font = TICK_FONT
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      for (const t of kWhTicks) {
        const py = kWhYS(t)
        ctx.fillStyle = 'rgba(128,128,128,0.85)'
        ctx.fillText(t % 1 === 0 ? String(t) : t.toFixed(1), plotW + 8, py)
      }
      // Right axis label "kWh"
      ctx.save()
      ctx.translate(plotW + 40, plotH / 2)
      ctx.rotate(Math.PI / 2)
      ctx.font = '11px sans-serif'
      ctx.fillStyle = 'rgba(128,128,128,0.85)'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('kWh', 0, 0)
      ctx.restore()
    }

    // ── X grid + ticks ─────────────────────────────────────────────────────

    const rangeMs = xMax - xMin
    const intervalH = rangeMs / 3_600_000 <= 12 ? 2 : 4
    const xTicks: Date[] = []
    const startDate = new Date(xMin)
    const startH = Math.ceil(startDate.getHours() / intervalH) * intervalH
    const tickStart = new Date(startDate)
    tickStart.setHours(startH, 0, 0, 0)
    for (let t = tickStart.getTime(); t <= xMax; t += intervalH * 3_600_000) {
      xTicks.push(new Date(t))
    }
    ctx.font = TICK_FONT
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    for (const t of xTicks) {
      const px = xS(t.getTime())
      ctx.strokeStyle = 'rgba(128,128,128,0.12)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(px, 0)
      ctx.lineTo(px, plotH)
      ctx.stroke()
      ctx.fillStyle = 'rgba(128,128,128,0.85)'
      ctx.fillText(format(t, 'HH:mm'), px, plotH + 8)
    }

    // ── Reference lines ────────────────────────────────────────────────────

    // Flat rate
    const flatY = yS(flatLineCt)
    ctx.strokeStyle = '#94a3b8'
    ctx.lineWidth = 1
    ctx.setLineDash([4, 2])
    ctx.beginPath()
    ctx.moveTo(0, flatY)
    ctx.lineTo(plotW, flatY)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.font = '10px sans-serif'
    ctx.fillStyle = '#94a3b8'
    ctx.textAlign = 'right'
    ctx.textBaseline = 'bottom'
    ctx.fillText(`Flat ${flatLineCt.toFixed(1)}ct`, plotW, flatY - 2)

    // Midnight boundaries for every day in the visible window
    {
      const DAY_MS = 86_400_000
      let t = startOfDay(new Date(xMin)).getTime()
      if (t < xMin) t += DAY_MS
      for (; t <= xMax; t += DAY_MS) {
        const midX = xS(t)
        if (midX < 0 || midX > plotW) continue
        ctx.strokeStyle = '#cbd5e1'
        ctx.lineWidth = 1.5
        ctx.setLineDash([])
        ctx.beginPath()
        ctx.moveTo(midX, 0)
        ctx.lineTo(midX, plotH)
        ctx.stroke()
        ctx.font = '10px sans-serif'
        ctx.fillStyle = '#94a3b8'
        ctx.textAlign = 'right'
        ctx.textBaseline = 'top'
        ctx.fillText(format(new Date(t), 'EEE, d MMM'), midX - 4, 2)
      }
    }

    // Session plug-in / depart lines (one pair per selected session)
    if (sessionOverlays.length > 0) {
      const drawVLine = (tsMs: number, label: string, align: CanvasTextAlign) => {
        const px = xS(tsMs)
        if (px < 0 || px > plotW) return
        ctx.strokeStyle = '#6366f1'
        ctx.lineWidth = 1.5
        ctx.setLineDash([4, 2])
        ctx.beginPath()
        ctx.moveTo(px, 0)
        ctx.lineTo(px, plotH)
        ctx.stroke()
        ctx.setLineDash([])
        ctx.font = '10px sans-serif'
        ctx.fillStyle = '#6366f1'
        ctx.textAlign = align
        ctx.textBaseline = 'top'
        ctx.fillText(label, align === 'left' ? px + 4 : px - 4, 2)
      }
      for (const overlay of sessionOverlays) {
        drawVLine(overlay.pluggedAtMs, 'Plug-in', 'left')
        drawVLine(overlay.unplugMs, 'Depart', 'right')
      }
    }

    // Now
    if (showNow) {
      const px = xS(now)
      ctx.strokeStyle = '#6366f1'
      ctx.lineWidth = 1
      ctx.setLineDash([3, 3])
      ctx.beginPath()
      ctx.moveTo(px, 0)
      ctx.lineTo(px, plotH)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.font = '10px sans-serif'
      ctx.fillStyle = '#6366f1'
      ctx.textAlign = 'left'
      ctx.textBaseline = 'top'
      ctx.fillText('Now', px + 4, 2)
    }

    // ── Series lines (clipped to plot area) ────────────────────────────────

    ctx.save()
    ctx.beginPath()
    ctx.rect(0, 0, plotW, plotH)
    ctx.clip()

    for (const s of allSeries) {
      if (hidden[s.key]) continue
      const pts = chartData
        .filter((d) => d[s.key] !== undefined)
        .map((d) => [xS(d.ts), yS(d[s.key]!)] as [number, number])
      if (pts.length < 2) continue
      ctx.strokeStyle = s.color
      ctx.lineWidth = 2
      ctx.lineJoin = 'round'
      ctx.setLineDash([])
      ctx.beginPath()
      ctx.moveTo(pts[0][0], pts[0][1])
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(pts[i][0], pts[i][1])
      }
      ctx.stroke()
    }

    ctx.restore()

    // ── Axes border ────────────────────────────────────────────────────────

    ctx.strokeStyle = 'rgba(128,128,128,0.35)'
    ctx.lineWidth = 1
    ctx.setLineDash([])
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(0, plotH)
    ctx.lineTo(plotW, plotH)
    ctx.stroke()

    ctx.restore()
  }, [chartData, bands, chargingSlots, sessionOverlays, flatRate, xDomain, hidden, width, buildScales, xMin, xMax, plotW, plotH, showDensity, showActual, slotDensity, maxSlotKwh, actualDensity, maxActualKwh, maxDensityKwh, allSeries])

  // ── Hover canvas: resize on dimension change ───────────────────────────────

  useEffect(() => {
    const canvas = hoverCanvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = width * dpr
    canvas.height = HEIGHT * dpr
  }, [width])

  // ── Hover canvas: draw crosshair when tooltip changes ─────────────────────

  useEffect(() => {
    const canvas = hoverCanvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (!tooltip) return
    const { xS } = buildScales()
    ctx.save()
    ctx.scale(dpr, dpr)
    ctx.translate(MARGIN.left, MARGIN.top)
    const px = xS(tooltip.ts)
    ctx.strokeStyle = 'rgba(128,128,128,0.6)'
    ctx.lineWidth = 1
    ctx.setLineDash([3, 3])
    ctx.beginPath()
    ctx.moveTo(px, 0)
    ctx.lineTo(px, plotH)
    ctx.stroke()
    ctx.restore()
  }, [tooltip, width, buildScales, plotH])

  // ── Mouse interaction ──────────────────────────────────────────────────────

  const tsSorted = useMemo(() => chartData.map((d) => d.ts), [chartData])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId)
      dragRef.current = { clientX: e.clientX, domain: xDomain }
      setIsPanning(false)
    },
    [xDomain]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!dragRef.current || !onDomainChange) return
      const dx = e.clientX - dragRef.current.clientX
      if (!isPanning && Math.abs(dx) < 4) return
      if (!isPanning) { setIsPanning(true); setTooltip(null) }
      const spanMs = dragRef.current.domain[1] - dragRef.current.domain[0]
      const msDelta = -(dx / plotW) * spanMs
      const newStart = dragRef.current.domain[0] + msDelta
      onDomainChange([newStart, newStart + spanMs])
    },
    [isPanning, plotW, onDomainChange]
  )

  const handlePointerUp = useCallback(() => {
    dragRef.current = null
    setIsPanning(false)
  }, [])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (isPanning || chartData.length === 0) return
      const svg = overlayRef.current
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const mx = e.clientX - rect.left - MARGIN.left
      const { xS } = buildScales()
      const tsAtMouse = xS.invert(mx)
      if (tsAtMouse < xMin || tsAtMouse > xMax) {
        setTooltip(null)
        return
      }
      const idx = Math.max(0, Math.min(d3.bisectLeft(tsSorted, tsAtMouse), chartData.length - 1))
      const point = chartData[idx]
      if (!point) return
      setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, ts: point.ts })
    },
    [chartData, tsSorted, buildScales, xMin, xMax]
  )

  const handleMouseLeave = useCallback(() => setTooltip(null), [])

  // ── Render ─────────────────────────────────────────────────────────────────

  const source = isEpex ? 'EPEX Spot' : 'SMARD.de'
  const subtitle =
    chartData.length > 0
      ? format(new Date(xMin), 'EEE d MMM, HH:mm') +
        ' – ' +
        format(new Date(xMax), 'EEE d MMM, HH:mm') +
        ` · ct/kWh · ${source}`
      : `ct/kWh · ${source}`

  const tooltipPoint = tooltip ? (chartData.find((d) => d.ts === tooltip.ts) ?? null) : null
  const densityAtHover = useMemo(() => {
    if (!tooltip) return null
    const slotMs = Math.floor(tooltip.ts / SLOT_MS) * SLOT_MS
    return {
      optimized: (showDensity && maxSlotKwh > 0) ? (slotDensity.get(slotMs) ?? null) : null,
      actual: (showActual && actualDensity && maxActualKwh > 0) ? (actualDensity.get(slotMs) ?? null) : null,
    }
  }, [tooltip, showDensity, showActual, slotDensity, maxSlotKwh, actualDensity, maxActualKwh])

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base">Price Curve</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            {allSeries.map(({ key, label, color }) => {
              const desc = SERIES_DESCRIPTIONS[key]
              const tip = desc ? (isEpex ? desc.epex : desc.smard) : undefined
              return (
                <button
                  key={key}
                  onClick={() => setHidden((prev) => ({ ...prev, [key]: !prev[key] }))}
                  className={cn(
                    'flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border transition-opacity',
                    hidden[key] ? 'opacity-40' : 'opacity-100'
                  )}
                >
                  <span
                    className="inline-block w-4 rounded-full"
                    style={{ backgroundColor: color, height: 2 }}
                  />
                  {label}
                  {tip && <InfoTip content={tip} side="bottom" />}
                </button>
              )
            })}
            {actualDensity && actualDensity.size > 0 && (
              <button
                onClick={() => setShowActual((v) => !v)}
                className={cn(
                  'flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border transition-opacity',
                  !showActual ? 'opacity-40' : 'opacity-100'
                )}
              >
                <span
                  className="inline-block w-4 h-2 rounded-sm shrink-0"
                  style={{ background: '#64748b' }}
                />
                Actual
                <InfoTip
                  content="Real charging phases reported by the charger, distributed proportionally into 15-min slots. Shows when charging actually happened."
                  side="bottom"
                />
              </button>
            )}
            {allSessions.length > 0 && (
              <button
                onClick={() => setShowDensity((v) => !v)}
                className={cn(
                  'flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border transition-opacity',
                  !showDensity ? 'opacity-40' : 'opacity-100'
                )}
              >
                <span
                  className="inline-block w-4 h-2 rounded-sm shrink-0"
                  style={{ background: 'linear-gradient(to right, #4575b4, #ffffbf, #d73027)' }}
                />
                Optimised
                <InfoTip
                  content="Simulated schedule: energy shifted into the cheapest Day-Ahead price slots within each session's plug-in window."
                  side="bottom"
                />
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="h-[280px] flex items-center justify-center text-sm text-muted-foreground">
            No price data for this window.
          </div>
        ) : (
          <div ref={containerRef} className="relative w-full select-none">
            {/* Main series canvas */}
            <canvas
              ref={canvasRef}
              style={{
                display: 'block',
                width: '100%',
                height: HEIGHT,
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
            {/* Hover crosshair canvas — sits above series, below SVG overlay */}
            <canvas
              ref={hoverCanvasRef}
              style={{
                display: 'block',
                width: '100%',
                height: HEIGHT,
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none',
              }}
            />
            {/* Spacer so the div has the right height */}
            <div style={{ height: HEIGHT }} />
            {/* SVG overlay — captures mouse events for crosshair + pointer events for panning */}
            <svg
              ref={overlayRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: HEIGHT,
                cursor: isPanning ? 'grabbing' : 'grab',
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            />
            {tooltip && tooltipPoint && (
              <div
                className="absolute pointer-events-none z-50 rounded-lg border bg-popover text-popover-foreground shadow-md text-sm"
                style={{
                  top: Math.max(0, tooltip.y - 10),
                  left: tooltip.x + 12 + 180 > width ? tooltip.x - 192 : tooltip.x + 12,
                  minWidth: 160,
                  padding: '10px 12px',
                }}
              >
                <p className="font-semibold mb-1.5">
                  {format(new Date(tooltipPoint.ts), 'EEE d MMM, HH:mm')}
                </p>
                {allSeries.filter(
                  (s) => !hidden[s.key] && tooltipPoint[s.key] !== undefined
                ).map((s) => (
                  <div key={s.key} className="flex items-center gap-1.5 mb-0.5">
                    <span style={{ color: s.color, fontSize: 10 }}>●</span>
                    <span className="flex-1">{s.label}:</span>
                    <span className="tabular-nums">{(tooltipPoint[s.key] as number).toFixed(2)} ct/kWh</span>
                  </div>
                ))}
                {(densityAtHover?.actual || densityAtHover?.optimized) && (
                  <div className="mt-1 pt-1 border-t border-border/50 space-y-0.5">
                    {densityAtHover.actual && (
                      <div className="flex items-center gap-1.5">
                        <span style={{ color: '#64748b', fontSize: 10 }}>●</span>
                        <span className="flex-1">Actual:</span>
                        <span className="tabular-nums">
                          {densityAtHover.actual.count} · {densityAtHover.actual.kWh.toFixed(2)} kWh
                        </span>
                      </div>
                    )}
                    {densityAtHover.optimized && (
                      <div className="flex items-center gap-1.5">
                        <span style={{ color: d3.interpolateRdYlBu(1 - densityAtHover.optimized.kWh / maxDensityKwh), fontSize: 10 }}>●</span>
                        <span className="flex-1">Optimised:</span>
                        <span className="tabular-nums">
                          {densityAtHover.optimized.count} · {densityAtHover.optimized.kWh.toFixed(2)} kWh
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
